<script>
import {
  exportMissingProvisionDocuments, getCarsWithMissingProvisionDocuments, ignoreCarsWithMissingProvisionDocuments, unignoreCarsWithMissingProvisionDocuments
} from '@/api/cars'
import LinkColumn from '@/components/ui/datatable/columns/LinkColumn.vue'
import FormattingHelpers from '@/mixins/FormattingHelpers'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import FeatureSelect from '@/components/cars/edit/FeatureSelect.vue'
import { getDocumentCategories } from '@/api/provisions'
import sortBy from 'lodash/sortBy'
import debounce from 'lodash/debounce'
import HighlightColumn from '@/components/ui/datatable/columns/HighlightColumn.vue'
import { DateTime } from 'luxon'
import CheckAllColumn from '@/components/ui/datatable/columns/CheckAllColumn.vue'
import CheckboxColumn from '@/components/ui/datatable/columns/CheckboxColumn.vue'
import DatatableActionBar from '@/components/ui/datatable/DatatableActionBar.vue'
import uniq from 'lodash/uniq'
import { TimeTracker } from '@/helpers/time-tracker'
import MissingProvisionDocumentsCommentColumn from '@/components/cars/MissingProvisionDocumentsCommentColumn.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import * as download from 'downloadjs'

export default {
  components: {
    'mia-datatable-action-bar': DatatableActionBar,
    'mia-feature-input': FeatureInput,
    'mia-feature-select': FeatureSelect,
    'mia-submit-button': SubmitButton
  },

  mixins: [FormattingHelpers],

  data() {
    return {
      cars: [],
      checkedIds: [],
      documentCategories: []
    }
  },

  computed: {
    query: {
      get() {
        return this.$route.query.q || ''
      },
      set(value) {
        this.setQueryDebounced(value)
      }
    },
    documentCategory: {
      get() {
        return this.$route.query.document || ''
      },
      set(value) {
        this.$router.replace({
          ...this.$route,
          query: {
            ...this.$route.query,
            document: value
          }
        })
      }
    },
    status: {
      get() {
        return this.$route.query.status || ''
      },
      set(value) {
        this.$router.replace({
          ...this.$route,
          query: {
            ...this.$route.query,
            status: value
          }
        })
      }
    },
    sortedDocumentCategories() {
      return sortBy(this.documentCategories, 'label')
    },
    isViewFiltered() {
      return this.query !== '' || this.documentCategory !== '' || this.status !== ''
    },
    selectedDocumentCategory() {
      if (!this.documentCategory) {
        return null
      }

      return this.documentCategories.find(category => category.id === this.documentCategory)
    },
    checkedCars() {
      if (!this.hasCheckedCars) {
        return this.cars
      }

      return this.cars.filter(car => this.checkedIds.includes(car.id))
    },
    checkedCarsCount() {
      return this.checkedIds.length
    },
    hasCheckedCars() {
      return this.checkedCarsCount > 0
    },
    canManage() {
      return this.authUser && this.authUser.item_is_allowed_manage_missing_provision_documents
    },
    columns() {
      return [
        ...(this.canManage ? [{
          field: '',
          headerComponent: CheckAllColumn,
          component: CheckboxColumn,
          align: 'center',
          meta: {
            only: 'id',
            isChecked: row => this.checkedIds.includes(row.id)
          }
        }] : []),
        {
          label: 'ID',
          field: 'id',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/cars/:id',
            external: true,
            highlight: () => (this.query ? this.query : '')
          }
        },
        {
          label: 'FIN',
          field: 'item_fin',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/cars/:id',
            external: true,
            labelProp: row => row.item_fin || 'Ohne FIN',
            highlight: () => (this.query ? this.query : '')
          }
        },
        {
          label: 'Fahrzeug',
          field: 'item_mobile_make',
          headerAlign: 'left',
          component: HighlightColumn,
          representedAs: row => `${row.item_mobile_make} ${row.item_mobile_model} ${row.item_shared_title.replace(new RegExp(`^${row.item_mobile_make}`, 'i'), '').trim().replace(new RegExp(`^${row.item_mobile_model}`, 'i'), '').trim()}`,
          meta: {
            highlight: () => (this.query ? this.query : '')
          }
        },
        {
          label: 'Händler',
          field: 'item_dealer_name',
          headerAlign: 'left'
        },
        {
          label: 'Verkäufer',
          field: 'item_sold_seller',
          headerAlign: 'left',
          representedAs: row => (row.item_sold_seller ? `${row.item_seller_firstname} ${row.item_seller_lastname}` : '')
        },
        {
          label: 'Kunde',
          field: 'item_customer',
          headerAlign: 'left',
          representedAs: row => (row.item_customer ? `${row.item_customer_firstname} ${row.item_customer_name}` : '')
        },
        {
          label: 'Datum Kundenbestellung',
          field: 'item_order_movement',
          headerAlign: 'left',
          representedAs: row => (row.item_order_movement ? this.formatDate(row.item_order_movement.item_created) : '')
        },
        {
          label: 'Datum Verkaufsmeldung',
          field: 'item_sell_movement',
          headerAlign: 'left',
          representedAs: row => (row.item_sell_movement ? this.formatDate(row.item_sell_movement.item_created) : '')
        },
        {
          label: 'Ausgeliefert',
          field: 'item_is_dispatched',
          headerAlign: 'left',
          representedAs: row => (row.item_is_dispatched ? '✅' : '')
        },
        {
          label: 'Fehlende Dokumente vor Auslieferung',
          field: '',
          headerAlign: 'left',
          sortable: false,
          component: HighlightColumn,
          representedAs: row => this.formatMissingDocumentsColumn(row),
          meta: {
            highlight: () => (this.selectedDocumentCategory ? this.selectedDocumentCategory.label : '')
          }
        },
        {
          label: 'Fehlende Dokumente nach Auslieferung',
          field: '',
          headerAlign: 'left',
          sortable: false,
          interpolate: true,
          representedAs: row => this.formatMissingDocumentsColumn(row, true)
        },
        {
          label: 'Als erledigt markiert',
          field: 'item_ignore_provision_documents_check',
          headerAlign: 'left',
          representedAs: row => (row.item_ignore_provision_documents_check === 'Y' ? '✅' : '')
        },
        {
          label: 'Kommentar',
          field: 'item_missing_provision_documents_comment',
          headerAlign: 'left',
          component: MissingProvisionDocumentsCommentColumn
        }
      ]
    }
  },

  watch: {
    '$route.query.q': function () {
      this.loadCars()
    },
    '$route.query.document': function () {
      this.loadCars()
    },
    '$route.query.status': function () {
      this.loadCars()
    }
  },

  created() {
    this.loadDocumentCategories()
    this.loadCars()
  },

  mounted() {
    window.eventBus.$emit('mia-loading-ended')
    window.eventBus.$emit('mia-set-breadcrumb', [
      {
        link: {
          path: '/legacy/cars'
        },
        text: 'Fahrzeuge'
      },
      {
        link: {
          path: '/cars/missing-provision-documents'
        },
        text: 'Fahrzeuge mit fehlenden Prämiendokumenten'
      }
    ])
  },

  methods: {
    formatDate(dateStr) {
      return DateTime.fromSQL(dateStr).toFormat('dd.MM.yyyy')
    },

    async loadDocumentCategories() {
      this.$wait.start('loading provision document categories')
      const [error, response] = await getDocumentCategories()
      this.$wait.end('loading provision document categories')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Dokumente konnten nicht geladen werden']
        })
        return
      }

      this.documentCategories = response.data
    },

    async loadCars() {
      this.$wait.start('loading cars with missing provision documents')
      const [error, cars] = await getCarsWithMissingProvisionDocuments({
        q: this.query || undefined,
        documentCategory: this.documentCategory || undefined,
        status: this.status || undefined
      })
      this.$wait.end('loading cars with missing provision documents')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Fahrzeuge konnten nicht geladen werden']
        })
        return
      }

      this.cars = cars
    },

    formatMissingDocumentsColumn(car, requiredAfterDispatch = false) {
      const documents = car.item_missing_documents.filter(doc => doc.required_after_dispatch === requiredAfterDispatch)

      if (documents.length === 0) {
        return ''
      }

      return `<ul class="list-disc pl-4">
        <li>${documents.map(doc => `${doc.label}
          <ul class="list-disc pl-4 text-sm">
            <li>${doc.provisions.map(provision => `<a href="/provisions/${provision.id}" target="_blank" class="hover:underline">${provision.name}</a>`).join('</li><li>')}</li>
          </ul>`).join('</li><li>')}</li>
      </ul>`
    },

    setQueryDebounced: debounce(function (query) {
      this.$router.replace({
        ...this.$route,
        query: {
          ...this.$route.query,
          q: query
        }
      })
    }, 1000),

    resetFilters() {
      this.$router.replace({
        ...this.$route,
        query: {
          ...this.$route.query,
          q: undefined,
          document: undefined
        }
      })
    },

    onChecked(checkedIds) {
      this.checkedIds = uniq([...this.checkedIds, ...checkedIds])
    },

    onUnchecked(uncheckedIds) {
      this.checkedIds = this.checkedIds.filter(checkedId => !uncheckedIds.includes(checkedId))
    },

    onSelectionCancel() {
      this.checkedIds = []
    },

    async markAsDone() {
      if (this.$wait.is('updating missing provision document entries')) {
        return
      }

      const tracker = new TimeTracker()

      this.$wait.start('updating missing provision document entries')
      tracker.capture()
      const [error] = await ignoreCarsWithMissingProvisionDocuments(this.checkedIds)
      tracker.capture()
      await tracker.wait()
      this.$wait.end('updating missing provision document entries')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Einträge konnte nicht als erledigt markiert werden']
        })
        return
      }

      window.eventBus.$emit('mia-info', {
        info: ['Einträge erfolgreich als erledigt markiert']
      })
      this.checkedIds = []
      this.loadCars()
    },

    async markAsUndone() {
      if (this.$wait.is('updating missing provision document entries')) {
        return
      }

      const tracker = new TimeTracker()

      this.$wait.start('updating missing provision document entries')
      tracker.capture()
      const [error] = await unignoreCarsWithMissingProvisionDocuments(this.checkedIds)
      tracker.capture()
      await tracker.wait()
      this.$wait.end('updating missing provision document entries')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Einträge konnte nicht als unerledigt markiert werden']
        })
        return
      }

      window.eventBus.$emit('mia-info', {
        info: ['Einträge erfolgreich als unerledigt markiert']
      })
      this.checkedIds = []
      this.loadCars()
    },

    async excelExport() {
      if (this.$wait.is('exporting')) {
        return
      }

      this.$wait.start('exporting')
      const [error, response] = await exportMissingProvisionDocuments({
        q: this.query || undefined,
        documentCategory: this.documentCategory || undefined,
        status: this.status || undefined
      })
      this.$wait.end('exporting')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Export fehlgeschlagen']
        })
        return
      }

      let fileName = `${DateTime.local().toFormat('yyyy-MM-dd')}-Fahrzeuge-mit-fehlenden-Prämiendokumenten.xlsx`

      if (response.headers['content-disposition']) {
        const matches = response.headers['content-disposition'].match(/filename="(.*?)"/)

        if (matches !== null) {
          fileName = matches[1].trim()
        }
      }

      download(response.data, fileName, response.data.type)

      window.eventBus.$emit('mia-info', {
        info: ['Daten erfolgreich exportiert']
      })
    }
  }
}
</script>

<template>
  <div class="w-full">
    <mia-has-feature feature="provisions">
      <h1 class="my-0 py-0">
        Fahrzeuge mit fehlenden Prämiendokumenten
      </h1>
      <div class="mt-4">
        <div class="bg-gray-300 rounded-t border border-gray-400 border-b-0 p-2">
          <div class="flex items-center">
            <div class="flex-1 grid grid-cols-4 gap-6">
              <div>
                <mia-feature-input
                  v-model="query"
                  label="Suchen"
                />
              </div>

              <div>
                <mia-feature-select
                  v-if="!$wait.is('loading provision document categories')"
                  v-model="documentCategory"
                  label="Dokument"
                >
                  <template #options>
                    <option value="">
                      Dokument auswählen
                    </option>
                    <option
                      v-for="category in sortedDocumentCategories"
                      :key="category.id"
                      :value="category.id"
                    >
                      {{ category.label }}
                    </option>
                  </template>
                </mia-feature-select>
              </div>

              <div>
                <mia-feature-select
                  v-if="!$wait.is('loading provision document categories')"
                  v-model="status"
                  label="Status"
                >
                  <template #options>
                    <option value="">
                      Erledigte ausschließen
                    </option>
                    <option value="ignored">
                      Nur erledigte anzeigen
                    </option>
                    <option value="all">
                      Alle anzeigen
                    </option>
                  </template>
                </mia-feature-select>
              </div>
              <div>
                <mia-submit-button
                  :disabled="$wait.any"
                  :is-loading="$wait.is('exporting')"
                  label="Excel Export"
                  class="export-button mt-6"
                  type="button"
                  size="small"
                  @click="excelExport"
                />
              </div>
            </div>
            <div class="pr-4 w-2/12 text-right">
              <button
                v-if="isViewFiltered"
                type="button"
                class="text-orange-mia hover:underline focus:outline-none"
                @click="resetFilters"
              >
                Filter zurücksetzen
              </button>
            </div>
          </div>
        </div>
        <mia-wait for="loading cars with missing provision documents">
          <template #waiting>
            <div class="flex items-center justify-center py-8">
              <mia-wave-loader />
            </div>
          </template>
          <template #default>
            <div class="w-full overflow-y-visible overflow-x-auto overflow-hidden">
              <datatable
                :columns="columns"
                :data="cars"
                class="sticky w-full table-auto datatable"
                @check-all="onChecked"
                @uncheck-all="onUnchecked"
                @checked="onChecked"
                @unchecked="onUnchecked"
              >
                <template #no-results>
                  <div class="flex justify-center">
                    Keine Fahrzeuge gefunden.
                  </div>
                </template>
              </datatable>
            </div>
          </template>
        </mia-wait>
      </div>

      <mia-datatable-action-bar
        v-if="canManage"
        :show="hasCheckedCars"
        :label="`${checkedCarsCount} gewählte${checkedCarsCount === 1 ? 'r' : ''} ${checkedCarsCount === 1 ? 'Eintrag' : 'Einträge'}`"
        :loading="$wait.is('updating missing provision document entries')"
        loading-text="Einträge werden gespeichert..."
        @cancel="onSelectionCancel"
      >
        <button
          type="button"
          class="bg-orange-mia hover:bg-orange-mia-dark text-white px-3 py-2 rounded-md text-sm focus:outline-none focus:shadow-outline-mia"
          @click="markAsDone"
        >
          Als erledigt markieren
        </button>
        <button
          type="button"
          class="bg-orange-mia hover:bg-orange-mia-dark text-white px-3 py-2 rounded-md text-sm focus:outline-none focus:shadow-outline-mia"
          @click="markAsUndone"
        >
          Als unerledigt markieren
        </button>
      </mia-datatable-action-bar>
    </mia-has-feature>
  </div>
</template>
